import { useFrame } from '@react-three/fiber';
import { useCallback, useEffect } from 'react';
import type { Object3D } from 'three';

import { usePlayerSettings, usePlayerStore } from '@cartwhl/3d/player';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';

import type { CartographerMotionPreviewGltfAsset } from '../motionPreviewAsset';

export interface CartographerMotionPreviewAssetPlayerProps {
  actorId: string;
  motionAsset: CartographerMotionPreviewGltfAsset;
  onSceneReady?: (scene: Object3D | null) => void;
}

export default function CartographerMotionPreviewAssetPlayer({
  actorId,
  motionAsset,
  onSceneReady,
}: CartographerMotionPreviewAssetPlayerProps) {
  const store = usePlayerStore();
  const {
    settings: {
      playback: {
        autoplay,
        disabled,
        frameRate,
        frameStepSize,
        loop,
        playbackSpeed,
      },
    },
  } = usePlayerSettings();
  const onReady = useCallback(
    (duration: { seconds: number; frames: number }) => {
      store.setDuration(duration);
    },
    [store],
  );
  const onPlayChange = useCallback(
    (isPlaying: boolean) => {
      store.setIsPlaying(isPlaying);
    },
    [store],
  );
  const onFrameUpdate = useCallback(
    (frame: number) => {
      store.setCurrentFrame(frame);
    },
    [store],
  );

  const mixer = useFrameMixer(motionAsset.clip, motionAsset.scene, {
    autoplay,
    disabled,
    frameRate,
    frameStepSize,
    loop,
    onFrameUpdate,
    onPlayChange,
    onReady,
    playbackSpeed,
  });

  useEffect(() => {
    store.registerActor(actorId);
    store.setActiveActor(actorId);
    store.setActorMeshStatus(actorId, 'ready');
    store.setActorAnimationStatus(actorId, 'ready');
    store.setActorAssetError(actorId, null);
    return () => {
      store.unregisterActor(actorId);
    };
  }, [actorId, store]);

  useEffect(() => {
    // GLTF preview asset lifetime is owned by useMotionPreviewGltfAsset.
    store.registerActorScene(actorId, motionAsset.scene);
    onSceneReady?.(motionAsset.scene);
    return () => {
      store.unregisterActorScene(actorId);
      onSceneReady?.(null);
    };
  }, [actorId, motionAsset.scene, onSceneReady, store]);

  useEffect(() => {
    store.setActorMixer(actorId, mixer);
    return () => {
      store.setActorMixer(actorId, undefined);
    };
  }, [actorId, mixer, store]);

  useEffect(() => {
    store.setDuration(mixer.duration);
  }, [mixer.duration, store]);

  // Keep native GLTF preview playback in lockstep with the existing BVH player path.
  useFrame((_, delta) => {
    mixer.update(delta);
  }, -1);

  return <primitive object={motionAsset.scene} />;
}
